package fr.manyverse.app.presentation.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AddPhotoAlternate
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material3.AssistChip
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.materialkolor.ktx.themeColor
import fr.manyverse.app.domain.model.CharacterMode
import fr.manyverse.app.domain.model.DiceFaceStyle
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.domain.model.GameModule
import fr.manyverse.app.domain.model.DEFAULT_THEME_ID
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.domain.model.UniverseDiceConfig
import fr.manyverse.app.domain.model.UniverseDie
import fr.manyverse.app.domain.model.UniverseFeatures
import fr.manyverse.app.domain.validation.UniverseValidator
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.rememberUniverseColorScheme
import fr.manyverse.app.presentation.viewmodel.UniversesViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.character_mode_free
import manyverse.shared.generated.resources.character_mode_template
import manyverse.shared.generated.resources.character_mode_template_coming_soon
import manyverse.shared.generated.resources.character_mode_template_dropdown_label
import manyverse.shared.generated.resources.feature_character_sheet
import manyverse.shared.generated.resources.feature_dice
import manyverse.shared.generated.resources.feature_inventory
import manyverse.shared.generated.resources.feature_messages
import manyverse.shared.generated.resources.feature_soundboard
import manyverse.shared.generated.resources.universe_character_section
import manyverse.shared.generated.resources.universe_dice_critical_failure
import manyverse.shared.generated.resources.universe_dice_critical_hint
import manyverse.shared.generated.resources.universe_dice_critical_section
import manyverse.shared.generated.resources.universe_dice_critical_success
import manyverse.shared.generated.resources.universe_dice_default_count
import manyverse.shared.generated.resources.universe_dice_face_style_label
import manyverse.shared.generated.resources.universe_dice_face_style_numerals
import manyverse.shared.generated.resources.universe_dice_face_style_skull
import manyverse.shared.generated.resources.universe_dice_modifiers_enabled
import manyverse.shared.generated.resources.universe_dice_multi_enabled
import manyverse.shared.generated.resources.universe_dice_section
import manyverse.shared.generated.resources.universe_dice_section_hint
import manyverse.shared.generated.resources.universe_dice_total_enabled
import manyverse.shared.generated.resources.universe_dice_winning_high
import manyverse.shared.generated.resources.universe_dice_winning_label
import manyverse.shared.generated.resources.universe_dice_winning_low
import manyverse.shared.generated.resources.universe_features_section
import manyverse.shared.generated.resources.universe_module_announcement
import manyverse.shared.generated.resources.universe_module_digicode
import manyverse.shared.generated.resources.universe_module_combat
import manyverse.shared.generated.resources.universe_module_gm_timer
import manyverse.shared.generated.resources.universe_module_lockpicking
import manyverse.shared.generated.resources.universe_module_timer
import manyverse.shared.generated.resources.universe_module_whiteboard
import manyverse.shared.generated.resources.universe_modules_section
import manyverse.shared.generated.resources.universe_name_label
import manyverse.shared.generated.resources.universe_save
import manyverse.shared.generated.resources.universe_theme_image_hint
import manyverse.shared.generated.resources.universe_theme_pick_image
import manyverse.shared.generated.resources.universe_theme_preview_dark
import manyverse.shared.generated.resources.universe_theme_preview_light
import manyverse.shared.generated.resources.universe_theme_reset
import manyverse.shared.generated.resources.universe_theme_section
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Universe creation/edit form (name, theme, features, modules, dice config).
 *
 * Extracted from `UniverseEditScreen` so it can be reused both as a full screen and as
 * the detail pane of a list-detail layout on large screens. The form owns all of its
 * field state (keyed on [initialUniverse]) and drives `UniversesViewModel.save`.
 *
 * @param editingId Universe id when editing, or null when creating.
 * @param initialUniverse Existing universe whose values seed the form, or null when creating.
 * @param onSaved Fired after a successful save.
 * @param onCancel When non-null, a cancel button is shown.
 */
@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun UniverseEditForm(
    userId: String,
    editingId: String?,
    initialUniverse: Universe?,
    modifier: Modifier = Modifier,
    viewModel: UniversesViewModel = koinViewModel(),
    onSaved: () -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val saveState by viewModel.saveState.collectAsStateWithLifecycle()

    var name by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.displayName.orEmpty())
    }
    var features by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.features ?: UniverseFeatures())
    }
    var modules by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.modules ?: GameModule.entries.toSet())
    }
    // Toujours trié par nombre de faces croissant : l'ordre est canonique en base
    // (les univers créés avant ce tri sont normalisés à la première réouverture).
    var availableDice by remember(initialUniverse) {
        mutableStateOf(
            (initialUniverse?.config?.dice?.available ?: defaultDiceForNew())
                .sortedBy { it.type.sides }
        )
    }
    var defaultCount by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.defaultCount ?: 1)
    }
    var multiEnabled by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.multiEnabled ?: false)
    }
    var modifiersEnabled by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.modifiersEnabled ?: false)
    }
    var totalEnabled by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.totalEnabled ?: true)
    }
    var highValuesWin by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.highValuesWin ?: true)
    }
    var diceFaceStyle by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.dice?.faceStyle ?: DiceFaceStyle.NUMERALS)
    }
    var themeSeedColor by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.themeSeedColor)
    }
    var characterMode by remember(initialUniverse) {
        mutableStateOf(initialUniverse?.config?.characterMode ?: CharacterMode.FREE_CONSTRUCTION)
    }
    var featuresExpanded by rememberSaveable { mutableStateOf(false) }
    var modulesExpanded by rememberSaveable { mutableStateOf(false) }
    var themeExpanded by rememberSaveable { mutableStateOf(false) }
    var characterModeExpanded by rememberSaveable { mutableStateOf(true) }

    LaunchedEffect(saveState) {
        if (saveState is AsyncUiState.Success) {
            viewModel.resetSaveState()
            onSaved()
        }
    }

    val config = remember(initialUniverse, name, features, modules, availableDice, defaultCount, multiEnabled, modifiersEnabled, totalEnabled, highValuesWin, diceFaceStyle, themeSeedColor, characterMode) {
        val defaultDie = availableDice.minByOrNull { it.type.sides }?.type ?: DieType.D20
        UniverseConfig(
            version = initialUniverse?.config?.version ?: 0,
            displayName = name.trim(),
            themeId = DEFAULT_THEME_ID,
            features = features,
            modules = modules,
            dice = if (features.dice) UniverseDiceConfig(
                available = availableDice,
                defaultDie = defaultDie,
                defaultCount = defaultCount,
                multiEnabled = multiEnabled,
                modifiersEnabled = modifiersEnabled,
                totalEnabled = totalEnabled,
                highValuesWin = highValuesWin,
                faceStyle = diceFaceStyle
            ) else UniverseDiceConfig(),
            currencySymbol = "$",
            characterMode = characterMode,
            themeSeedColor = themeSeedColor
        )
    }
    val canSave = UniverseValidator.validateForCreation(name, config).isSuccess
        && saveState !is AsyncUiState.Loading
        && characterMode != CharacterMode.TEMPLATE

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        OutlinedTextField(
            value = name,
            onValueChange = { if (it.length <= UniverseValidator.MAX_NAME_LENGTH) name = it },
            label = { Text(stringResource(Res.string.universe_name_label)) },
            singleLine = true,
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
            supportingText = { Text("${name.length}/${UniverseValidator.MAX_NAME_LENGTH}") },
            modifier = Modifier.fillMaxWidth()
        )

        CollapsibleSectionTitle(
            text = stringResource(Res.string.universe_theme_section),
            expanded = themeExpanded,
            onToggle = { themeExpanded = !themeExpanded },
            swatchColor = themeSeedColor?.let { Color(it) } ?: MaterialTheme.colorScheme.primary
        )
        AnimatedVisibility(visible = themeExpanded) {
            ThemeColorSection(
                seedColor = themeSeedColor,
                onSeedChange = { themeSeedColor = it }
            )
        }

        HorizontalDivider()

        val activeFeatures = listOf(
            features.inventory, features.characterSheet, features.dice,
            features.soundboard, features.messages
        ).count { it }
        CollapsibleSectionTitle(
            text = "${stringResource(Res.string.universe_features_section)} ($activeFeatures/5)",
            expanded = featuresExpanded,
            onToggle = { featuresExpanded = !featuresExpanded }
        )
        AnimatedVisibility(visible = featuresExpanded) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                FeatureToggle(stringResource(Res.string.feature_inventory), features.inventory) {
                    features = features.copy(inventory = it)
                }
                FeatureToggle(stringResource(Res.string.feature_character_sheet), features.characterSheet) {
                    features = features.copy(characterSheet = it)
                }
                FeatureToggle(stringResource(Res.string.feature_dice), features.dice) {
                    features = features.copy(dice = it)
                }
                FeatureToggle(stringResource(Res.string.feature_soundboard), features.soundboard) {
                    features = features.copy(soundboard = it)
                }
                FeatureToggle(stringResource(Res.string.feature_messages), features.messages) {
                    features = features.copy(messages = it)
                }
            }
        }

        HorizontalDivider()

        CollapsibleSectionTitle(
            text = "${stringResource(Res.string.universe_modules_section)} (${modules.size}/${GameModule.entries.size})",
            expanded = modulesExpanded,
            onToggle = { modulesExpanded = !modulesExpanded }
        )
        AnimatedVisibility(visible = modulesExpanded) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                GameModule.entries.forEach { module ->
                    val label = stringResource(module.labelRes())
                    FeatureToggle(label, module in modules) { checked ->
                        modules = if (checked) modules + module else modules - module
                    }
                }
            }
        }

        if (features.characterSheet) {
            HorizontalDivider()

            CollapsibleSectionTitle(
                text = stringResource(Res.string.universe_character_section),
                expanded = characterModeExpanded,
                onToggle = { characterModeExpanded = !characterModeExpanded }
            )
            AnimatedVisibility(visible = characterModeExpanded) {
                CharacterModeSection(
                    characterMode = characterMode,
                    onModeChange = { characterMode = it }
                )
            }
        }

        if (features.dice) {
            HorizontalDivider()

            DiceConfigSection(
                availableDice = availableDice,
                onAvailableDiceChange = { availableDice = it },
                defaultCount = defaultCount,
                onDefaultCountChange = { defaultCount = it },
                multiEnabled = multiEnabled,
                onMultiEnabledChange = { multiEnabled = it },
                modifiersEnabled = modifiersEnabled,
                onModifiersEnabledChange = { modifiersEnabled = it },
                totalEnabled = totalEnabled,
                onTotalEnabledChange = { totalEnabled = it },
                highValuesWin = highValuesWin,
                onHighValuesWinChange = { highValuesWin = it },
                faceStyle = diceFaceStyle,
                onFaceStyleChange = { diceFaceStyle = it }
            )
        }

        (saveState as? AsyncUiState.Error)?.let {
            Text(it.message, color = MaterialTheme.colorScheme.error)
        }

        Button(
            onClick = { viewModel.save(name, config, userId, editingId) },
            enabled = canSave,
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(stringResource(Res.string.universe_save))
        }

        if (onCancel != null) {
            TextButton(
                onClick = onCancel,
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(stringResource(Res.string.back))
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(text, style = MaterialTheme.typography.titleMedium)
}

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
private fun DiceConfigSection(
    availableDice: List<UniverseDie>,
    onAvailableDiceChange: (List<UniverseDie>) -> Unit,
    defaultCount: Int,
    onDefaultCountChange: (Int) -> Unit,
    multiEnabled: Boolean,
    onMultiEnabledChange: (Boolean) -> Unit,
    modifiersEnabled: Boolean,
    onModifiersEnabledChange: (Boolean) -> Unit,
    totalEnabled: Boolean,
    onTotalEnabledChange: (Boolean) -> Unit,
    highValuesWin: Boolean,
    onHighValuesWinChange: (Boolean) -> Unit,
    faceStyle: DiceFaceStyle,
    onFaceStyleChange: (DiceFaceStyle) -> Unit
) {
    var advancedExpanded by remember { mutableStateOf(false) }

    SectionTitle(stringResource(Res.string.universe_dice_section))
    Text(
        text = stringResource(Res.string.universe_dice_section_hint),
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        DieType.entries.forEach { type ->
            val selected = availableDice.any { it.type == type }
            FilterChip(
                selected = selected,
                onClick = {
                    onAvailableDiceChange(
                        if (selected) availableDice.filter { it.type != type }
                        else (availableDice + UniverseDie(type)).sortedBy { it.type.sides }
                    )
                },
                label = { Text(type.name) }
            )
        }
    }

    Text(
        text = stringResource(Res.string.universe_dice_winning_label),
        style = MaterialTheme.typography.titleSmall,
        modifier = Modifier.padding(top = 8.dp)
    )
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        FilterChip(
            selected = highValuesWin,
            onClick = { onHighValuesWinChange(true) },
            label = { Text(stringResource(Res.string.universe_dice_winning_high)) }
        )
        FilterChip(
            selected = !highValuesWin,
            onClick = { onHighValuesWinChange(false) },
            label = { Text(stringResource(Res.string.universe_dice_winning_low)) }
        )
    }

    Text(
        text = stringResource(Res.string.universe_dice_face_style_label),
        style = MaterialTheme.typography.titleSmall,
        modifier = Modifier.padding(top = 8.dp)
    )
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        FilterChip(
            selected = faceStyle == DiceFaceStyle.NUMERALS,
            onClick = { onFaceStyleChange(DiceFaceStyle.NUMERALS) },
            label = { Text(stringResource(Res.string.universe_dice_face_style_numerals)) }
        )
        FilterChip(
            selected = faceStyle == DiceFaceStyle.SKULL_ONE,
            onClick = { onFaceStyleChange(DiceFaceStyle.SKULL_ONE) },
            label = { Text(stringResource(Res.string.universe_dice_face_style_skull)) }
        )
    }

    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp),
        modifier = Modifier.padding(top = 8.dp)
    ) {
        FeatureToggle(stringResource(Res.string.universe_dice_multi_enabled), multiEnabled) {
            onMultiEnabledChange(it)
        }
        AnimatedVisibility(visible = multiEnabled) {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = defaultCount.toString(),
                    onValueChange = { newValue ->
                        newValue.toIntOrNull()?.let { onDefaultCountChange(it.coerceAtLeast(1)) }
                    },
                    label = { Text(stringResource(Res.string.universe_dice_default_count)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                FeatureToggle(stringResource(Res.string.universe_dice_total_enabled), totalEnabled) {
                    onTotalEnabledChange(it)
                }
            }
        }
        FeatureToggle(stringResource(Res.string.universe_dice_modifiers_enabled), modifiersEnabled) {
            onModifiersEnabledChange(it)
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        AssistChip(
            onClick = { advancedExpanded = !advancedExpanded },
            label = { Text(stringResource(Res.string.universe_dice_critical_section)) },
            trailingIcon = {
                Icon(
                    if (advancedExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
            }
        )
    }
    AnimatedVisibility(visible = advancedExpanded) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(
                text = stringResource(Res.string.universe_dice_critical_hint),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            availableDice.forEach { die ->
                // Les seuils sont stockés bruts (criticalMin = borne basse,
                // criticalMax = borne haute). Selon le sens de victoire, l'échec
                // et la réussite sont liés à l'une ou l'autre des bornes.
                val failureValue = if (highValuesWin) die.criticalMin else die.criticalMax
                val successValue = if (highValuesWin) die.criticalMax else die.criticalMin
                Text(
                    text = die.type.name,
                    style = MaterialTheme.typography.titleSmall
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = failureValue?.toString().orEmpty(),
                        onValueChange = { newValue ->
                            val parsed = newValue.toIntOrNull()
                            onAvailableDiceChange(
                                availableDice.map {
                                    if (it.type != die.type) it
                                    else if (highValuesWin) it.copy(criticalMin = parsed)
                                    else it.copy(criticalMax = parsed)
                                }
                            )
                        },
                        label = { Text(stringResource(Res.string.universe_dice_critical_failure)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        singleLine = true,
                        modifier = Modifier.weight(1f)
                    )
                    OutlinedTextField(
                        value = successValue?.toString().orEmpty(),
                        onValueChange = { newValue ->
                            val parsed = newValue.toIntOrNull()
                            onAvailableDiceChange(
                                availableDice.map {
                                    if (it.type != die.type) it
                                    else if (highValuesWin) it.copy(criticalMax = parsed)
                                    else it.copy(criticalMin = parsed)
                                }
                            )
                        },
                        label = { Text(stringResource(Res.string.universe_dice_critical_success)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        singleLine = true,
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
private fun CharacterModeSection(
    characterMode: CharacterMode,
    onModeChange: (CharacterMode) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            FilterChip(
                selected = characterMode == CharacterMode.FREE_CONSTRUCTION,
                onClick = { onModeChange(CharacterMode.FREE_CONSTRUCTION) },
                label = { Text(stringResource(Res.string.character_mode_free)) }
            )
            FilterChip(
                selected = characterMode == CharacterMode.TEMPLATE,
                onClick = { onModeChange(CharacterMode.TEMPLATE) },
                label = { Text(stringResource(Res.string.character_mode_template)) }
            )
        }

        AnimatedVisibility(visible = characterMode == CharacterMode.TEMPLATE) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                var expanded by remember { mutableStateOf(false) }
                ExposedDropdownMenuBox(
                    expanded = expanded,
                    onExpandedChange = { expanded = !expanded },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    OutlinedTextField(
                        value = "",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.character_mode_template_dropdown_label)) },
                        trailingIcon = {
                            ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded)
                        },
                        colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
                        modifier = Modifier
                            .fillMaxWidth()
                            .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryEditable, true)
                    )
                    ExposedDropdownMenu(
                        expanded = expanded,
                        onDismissRequest = { expanded = false }
                    ) {
                        // Aucun template disponible pour le moment.
                    }
                }
                Text(
                    text = stringResource(Res.string.character_mode_template_coming_soon),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun CollapsibleSectionTitle(
    text: String,
    expanded: Boolean,
    onToggle: () -> Unit,
    swatchColor: Color? = null
) {
    Row(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onToggle),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(text, style = MaterialTheme.typography.titleMedium, modifier = Modifier.weight(1f))
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            if (swatchColor != null) {
                Box(
                    modifier = Modifier
                        .size(16.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(swatchColor)
                )
            }
            Icon(
                if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                contentDescription = null,
                modifier = Modifier.size(18.dp)
            )
        }
    }
}

@Composable
private fun ThemeColorSection(
    seedColor: Long?,
    onSeedChange: (Long?) -> Unit
) {
    val defaultColor = MaterialTheme.colorScheme.primary
    val currentColor = seedColor?.let { Color(it) } ?: defaultColor

    val pickImage = rememberImagePickerLauncher { imageData ->
        runCatching { imageData.bytes.decodeToImageBitmap().themeColor(fallback = defaultColor) }
            .getOrNull()
            ?.let { onSeedChange(it.toArgb().toLong() and 0xFFFFFFFFL) }
    }

    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        HsvColorPicker(
            color = currentColor,
            onColorChanged = { onSeedChange(it.toArgb().toLong() and 0xFFFFFFFFL) },
            modifier = Modifier.fillMaxWidth()
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            OutlinedButton(onClick = pickImage, modifier = Modifier.weight(1f)) {
                Icon(
                    imageVector = Icons.Filled.AddPhotoAlternate,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Text(
                    text = stringResource(Res.string.universe_theme_pick_image),
                    modifier = Modifier.padding(start = 8.dp)
                )
            }
            if (seedColor != null) {
                OutlinedButton(onClick = { onSeedChange(null) }, modifier = Modifier.weight(1f)) {
                    Text(stringResource(Res.string.universe_theme_reset))
                }
            }
        }

        Text(
            text = stringResource(Res.string.universe_theme_image_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        val previewConfig = UniverseConfig(themeSeedColor = seedColor)
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            ThemePreviewCard(
                config = previewConfig,
                isDark = false,
                label = stringResource(Res.string.universe_theme_preview_light),
                modifier = Modifier.weight(1f)
            )
            ThemePreviewCard(
                config = previewConfig,
                isDark = true,
                label = stringResource(Res.string.universe_theme_preview_dark),
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ThemePreviewCard(
    config: UniverseConfig,
    isDark: Boolean,
    label: String,
    modifier: Modifier = Modifier
) {
    val scheme = rememberUniverseColorScheme(config, isDark)
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(label, style = MaterialTheme.typography.labelSmall)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(8.dp))
                .background(scheme.background)
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            listOf(
                scheme.primary,
                scheme.primaryContainer,
                scheme.secondary,
                scheme.tertiary
            ).forEach { swatch ->
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(28.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(swatch)
                )
            }
        }
    }
}

@Composable
private fun FeatureToggle(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    SwitchRow(
        label = label,
        checked = checked,
        onCheckedChange = onChange,
        textStyle = MaterialTheme.typography.bodyLarge
    )
}

private fun GameModule.labelRes(): StringResource = when (this) {
    GameModule.ANNOUNCEMENT -> Res.string.universe_module_announcement
    GameModule.WHITEBOARD -> Res.string.universe_module_whiteboard
    GameModule.TIMER -> Res.string.universe_module_timer
    GameModule.DIGICODE -> Res.string.universe_module_digicode
    GameModule.LOCKPICKING -> Res.string.universe_module_lockpicking
    GameModule.GM_TIMER -> Res.string.universe_module_gm_timer
    GameModule.COMBAT -> Res.string.universe_module_combat
}

private fun defaultDiceForNew(): List<UniverseDie> = emptyList()
